import { Badge, Button, Group, Indicator, Menu, Stack, Text } from '@mantine/core';
import { Link } from 'react-router';
import { BILDIRIM_HEDEF_HARITASI, BILDIRIM_OZET_TARIH_ANAHTARLARI } from '@isg/validation';
import type { BildirimTipi } from '@isg/validation';
import { useZil, useZilOkundu } from '../api/queries';
import { BILDIRIM_TIP_ETIKET } from '../i18n/tr';
import { ANLAM } from '../theme';
import { tarihGoster } from '../lib/format';

/**
 * Uygulama içi bildirim zili (F2·S2 — Kapsam §22.2/8, S1'de ertelenen ekran):
 * okunmamış sayaç + son teslimatlar + okundu işareti. Veri kullanıcının KENDİ
 * in_app teslimatlarıdır (sunucu kimlikten süzer); e-postasız kullanıcı
 * bildirimi bu kanaldan alır. Okundu işareti self-service yazmadır — izleyici
 * genel kuralla yazamaz, düğme onda gizlenir (karar yine sunucuda).
 *
 * ✏️ **BULGU 103 (K-103a…e): SATIR KAYNAĞINA GİDER.** Yol SUNUCUDAN gelir
 * (`kalem.hedef` — haritadan çözülür; türetilen sınıfın üst kaydı ve sahip
 * kolu yalnız sunucuda bilinir) — ekran bağ KURMAZ, çizer. Tıklama okundu
 * işaretlemez (davranış değişmedi). Satır metni kaydı tanıtır: tip başına
 * anahtar listesi aynı haritada (K-103d; metin olay anında dondurulmuştur).
 */
export function ZilMenusu({ salt }: { salt: boolean }): React.JSX.Element {
  const zil = useZil();
  const okundu = useZilOkundu();
  const okunmamis = zil.data?.okunmamis ?? 0;

  return (
    <Menu shadow="md" width={380} position="bottom-end">
      <Menu.Target>
        <Indicator
          label={okunmamis > 99 ? '99+' : okunmamis}
          size={16}
          disabled={okunmamis === 0}
          color={ANLAM.engel}
          data-testid="zil-sayac"
        >
          <Button size="xs" variant="default" data-testid="zil-dugmesi" aria-label="Bildirimler">
            🔔
          </Button>
        </Indicator>
      </Menu.Target>
      <Menu.Dropdown data-testid="zil-listesi">
        <Group justify="space-between" px="sm" py={4}>
          <Text size="sm" fw={600}>
            Bildirimler
          </Text>
          {!salt && okunmamis > 0 && (
            <Button
              size="compact-xs"
              variant="subtle"
              data-testid="okundu-isaretle"
              loading={okundu.isPending}
              onClick={() => okundu.mutate({})}
            >
              Tümünü okundu işaretle
            </Button>
          )}
        </Group>
        {zil.data && zil.data.kalemler.length === 0 && (
          <Text size="sm" c="dimmed" px="sm" py="xs" data-testid="zil-bos">
            Bildiriminiz yok.
          </Text>
        )}
        <Stack gap={0} mah={360} style={{ overflowY: 'auto' }}>
          {(zil.data?.kalemler ?? []).map((kalem) => (
            <Menu.Item
              key={kalem.deliveryId}
              component={Link}
              to={kalem.hedef}
              px="sm"
              py={6}
              data-testid="zil-kalemi"
              data-hedef={kalem.hedef}
            >
              <Stack gap={2}>
                <Group gap="xs">
                  {!kalem.okunduAt && (
                    <Badge
                      size="xs"
                      color={ANLAM.engel}
                      variant="filled"
                      data-testid="okunmamis-rozeti"
                    >
                      Yeni
                    </Badge>
                  )}
                  <Text size="sm" fw={kalem.okunduAt ? 400 : 600}>
                    {BILDIRIM_TIP_ETIKET[kalem.tip as BildirimTipi] ?? 'Bildirim'}
                  </Text>
                </Group>
                <Text size="xs" c="dimmed" data-testid="zil-kalemi-ozet">
                  {zilOzeti(kalem.tip, kalem.icerik)} · {tarihGoster(kalem.olayGunu)}
                </Text>
              </Stack>
            </Menu.Item>
          ))}
        </Stack>
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * İçerik jsonb'sinden okunur tek satır — anahtar listesi TİPİN haritasından
 * (K-103d). Alan yoksa geçilir.
 *
 * ⚠️ **G11:** tarih anahtarları ISO GÜNÜDÜR (`notifications.ts` `toDateOnly`
 * ile yazar) — kullanıcı biçimine burada çevrilir; diğerleri serbest metin ya
 * da sayıdır. Zilde `2026-09-26`, listede `26.09.2026` görünmesi G11'in
 * kapattığı arızaydı.
 */
const ZIL_TARIH_ANAHTARLARI = new Set(BILDIRIM_OZET_TARIH_ANAHTARLARI);

function zilOzeti(tip: string, icerik: Record<string, unknown>): string {
  const anahtarlar = BILDIRIM_HEDEF_HARITASI[tip as BildirimTipi]?.ozet ?? [];
  const parcalar = anahtarlar
    .map((anahtar) => [anahtar, icerik[anahtar]] as const)
    .filter((cift): cift is readonly [string, string | number] =>
      typeof cift[1] === 'string' ? cift[1].length > 0 : typeof cift[1] === 'number',
    )
    .map(([anahtar, deger]) =>
      ZIL_TARIH_ANAHTARLARI.has(anahtar) && typeof deger === 'string'
        ? tarihGoster(deger)
        : String(deger),
    );
  return parcalar.length > 0 ? parcalar.join(' · ') : '—';
}
